import { ReactNode } from 'react';

import { Slide } from '@components/PresentationRebrand';
import {
  AwardWinningServices,
  CashbackCard,
  GoUnlimited,
  IntroducingUtilityWarehouse,
  MonthlyIncome,
  WelcomeBonus,
} from '@components/PresentationRebrand/ContentSlide/slides';
import { Households } from '@components/PresentationRebrand/ContentSlide/slides/Households';
import { SlideId } from '@components/PresentationRebrand/constants';
import { EXTERNAL_LINKS } from '@constants/externalLinks';

import desktop03New from '/Presentation/Desktop/Slide_03_New_v2.png';
import desktop04New from '/Presentation/Desktop/Slide_04_New.png';
import desktop07New from '/Presentation/Desktop/Slide_07_New.png';
import desktop08New from '/Presentation/Desktop/Slide_08_New.png';
import desktop09New from '/Presentation/Desktop/Slide_09_New.png';
import desktop10New from '/Presentation/Desktop/Slide_10_New.png';
import desktop11New from '/Presentation/Desktop/Slide_11_New.png';
import desktop13New from '/Presentation/Desktop/Slide_13_New.png';
import desktop14New from '/Presentation/Desktop/Slide_14_New.png';
import desktopGoUnlimited from '/Presentation/Desktop/Slide_Go_Unlimited.png';
import mobile03New from '/Presentation/Mobile/Variable Height_Slide_03_New_v2.png';
import mobile04New from '/Presentation/Mobile/Variable Height_Slide_04_New.png';
import mobile07New from '/Presentation/Mobile/Variable Height_Slide_07_New.png';
import mobile08New from '/Presentation/Mobile/Variable Height_Slide_08_New.png';
import mobile09New from '/Presentation/Mobile/Variable Height_Slide_09_New.png';
import mobile10New from '/Presentation/Mobile/Variable Height_Slide_10_New.png';
import mobile11New from '/Presentation/Mobile/Variable Height_Slide_11_New.png';
import mobile13New from '/Presentation/Mobile/Variable Height_Slide_13_New.png';
import mobile14New from '/Presentation/Mobile/Variable Height_Slide_14_New.png';
import mobileGoUnlimited from '/Presentation/Mobile/Variable Height_Slide_Go_Unlimited.png';

const contentSlide = (id: string | SlideId, children: ReactNode): Slide => ({
  id,
  type: 'content',
  children,
});

const callToActionSlide = (
  id: string | SlideId,
  heading: string,
  buttons: NonNullable<Slide['buttons']>
): Slide => ({
  id,
  type: 'callToAction',
  colorScheme: 'purple',
  heading,
  buttons,
});

const videoSlide = (
  id: string | SlideId,
  vimeoId: string,
  vimeoHash: string,
  videoHeadline: string,
  videoSubheadline: string
): Slide => ({
  id,
  type: 'video',
  colorScheme: 'purple',
  vimeoId,
  vimeoHash,
  videoHeadline,
  videoSubheadline,
});

const imageSlide = (
  id: string | SlideId,
  desktopImageUrl: string,
  mobileImageUrl: string,
  termsText: string
): Slide => ({
  id,
  type: 'image',
  colorScheme: 'cream',
  desktopImageUrl,
  mobileImageUrl,
  termsText,
});

const WHERE_TO_START_BUTTONS: NonNullable<Slide['buttons']> = [
  { id: 'save-as-customer', label: 'Save as a customer', type: 'quote' },
  {
    id: 'earn-as-partner',
    label: 'Earn as a Partner',
    type: 'external',
    externalUrl: EXTERNAL_LINKS.partnerJoin,
  },
  { id: 'or-both', label: 'Or both', type: 'quote' },
];

/**
 * @deprecated legacy image slides are being replaced by reworkSlides.
 */
const legacySlides: Slide[] = [
  callToActionSlide(
    'slide-1',
    'People helping people save money on their bills',
    [
      {
        id: 'hit-play',
        label: 'Hit play to learn more',
        type: 'slide',
        slideIndex: 1,
      },
      {
        id: 'partner-shows-how',
        label: 'Your UW Partner shows you how',
        type: 'slide',
        slideIndex: 5,
      },
    ]
  ),
  videoSlide(
    'slide-2',
    '1183386513',
    'cdaab8f0f5',
    "Find out why Cat Deeley's so excited about Utility Warehouse",
    "Here's how Utility Warehouse is different"
  ),
  imageSlide('slide-3', desktop03New, mobile03New, "T&C's apply"),
  imageSlide('slide-4', desktop04New, mobile04New, "T&C's apply"),
  callToActionSlide('slide-5', 'Where shall we start?', WHERE_TO_START_BUTTONS),
  videoSlide(
    'slide-6',
    '1183386513',
    'cdaab8f0f5',
    "Find out why Cat Deeley's so excited about Utility Warehouse",
    "Here's how Utility Warehouse is different"
  ),
  imageSlide('slide-7', desktop07New, mobile07New, "T&C's apply"),
  imageSlide('slide-7a', desktopGoUnlimited, mobileGoUnlimited, "T&C's apply"),

  imageSlide('slide-8', desktop08New, mobile08New, "T&C's apply"),
  imageSlide('slide-9', desktop09New, mobile09New, "T&C's apply."),
  imageSlide('slide-10', desktop10New, mobile10New, "T&C's apply"),
  imageSlide('slide-11', desktop11New, mobile11New, "T&C's apply"),
  videoSlide(
    'slide-12',
    '1183400404',
    '96bfdcaa16',
    'How you can earn more than an income',
    'Becoming a Utility Warehouse Partner'
  ),
  imageSlide('slide-13', desktop13New, mobile13New, "T&C's apply"),
  imageSlide('slide-14', desktop14New, mobile14New, ''),
  callToActionSlide('slide-15', "What's best for you?", WHERE_TO_START_BUTTONS),
];

const reworkSlides: Slide[] = [
  callToActionSlide(
    SlideId.Introduction,
    'People helping people save money on their bills',
    [
      {
        id: 'hit-play',
        label: 'Hit play to learn more',
        type: 'slide',
        slideIndex: 1,
      },
      {
        id: 'partner-shows-how',
        label: 'Your UW Partner shows you how',
        type: 'slide',
        slideIndex: 5,
      },
    ]
  ),
  videoSlide(
    SlideId.CatDeeleyVideo,
    '1183386513',
    'cdaab8f0f5',
    "Find out why Cat Deeley's so excited about Utility Warehouse",
    "Here's how Utility Warehouse is different"
  ),
  contentSlide(
    SlideId.IntroducingUtilityWarehouse,
    <IntroducingUtilityWarehouse />
  ),
  contentSlide(SlideId.MonthlyIncome, <MonthlyIncome />),
  callToActionSlide(
    SlideId.WhereShallWeStart,
    'Where shall we start?',
    WHERE_TO_START_BUTTONS
  ),
  videoSlide(
    SlideId.CatDeeleyVideoB,
    '1183386513',
    'cdaab8f0f5',
    "Find out why Cat Deeley's so excited about Utility Warehouse",
    "Here's how Utility Warehouse is different"
  ),
  contentSlide(SlideId.AwardWinningServices, <AwardWinningServices />),
  contentSlide(SlideId.GoUnlimited, <GoUnlimited />),
  contentSlide(SlideId.CashbackCard, <CashbackCard />),
  contentSlide(SlideId.WelcomeBonus, <WelcomeBonus />),
  contentSlide(SlideId.Households, <Households />),
];

const getPartnerPresentationSlides = (
  isPartnerPresentationReworkEnabled: boolean
): Slide[] =>
  isPartnerPresentationReworkEnabled ? reworkSlides : legacySlides;

export { getPartnerPresentationSlides };
